// Webflow Tabs widget (.w-tabs). Tab links and panes are matched by `data-w-tab`; the initial
// tab comes from `data-current`. Panes fade according to `data-duration-in/out`.

import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cx, type WfProps, withoutClass } from './element';

interface TabsState {
  current: string;
  setCurrent: (tab: string) => void;
  durationIn: number;
  easing: string;
}

const TabsContext = createContext<TabsState | null>(null);

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function Tabs({ as: Tag = 'div', children, ...props }: WfProps) {
  const [current, setCurrent] = useState(props['data-current'] ?? '');
  return (
    <TabsContext.Provider
      value={{
        current,
        setCurrent,
        durationIn: Number(props['data-duration-in'] ?? 300),
        easing: props['data-easing'] ?? 'ease',
      }}
    >
      <Tag {...props}>{children}</Tag>
    </TabsContext.Provider>
  );
}

export function TabLink({ as: Tag = 'a', className, children, ...props }: WfProps) {
  const tabs = useContext(TabsContext);
  const tab = props['data-w-tab'] ?? '';
  const active = tabs?.current === tab;
  return (
    <Tag
      {...props}
      className={cx(withoutClass(className, 'w--current'), active && 'w--current')}
      role="tab"
      aria-selected={active}
      tabIndex={active ? undefined : -1}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        tabs?.setCurrent(tab);
      }}
    >
      {children}
    </Tag>
  );
}

/**
 * The panes container. With `morphHeight` (the home page's custom code), its height follows the
 * active pane so a CSS height transition can animate tab switches.
 */
export function TabContent({ as: Tag = 'div', morphHeight, children, ...props }: WfProps & { morphHeight?: boolean }) {
  const tabs = useContext(TabsContext);
  const ref = useRef<HTMLElement>(null);
  const current = tabs?.current;

  useEffect(() => {
    const el = ref.current;
    if (!morphHeight || !el) return undefined;
    const fit = () => {
      const active = el.querySelector<HTMLElement>('.w-tab-pane.w--tab-active');
      if (active) el.style.height = `${active.offsetHeight}px`;
    };
    fit();
    // Measure again once web fonts have settled.
    const t = setTimeout(fit, 500);
    window.addEventListener('resize', fit);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', fit);
    };
  }, [morphHeight, current]);

  return (
    <Tag ref={ref} {...props}>
      {children}
    </Tag>
  );
}

export function TabPane({ as: Tag = 'div', className, children, ...props }: WfProps) {
  const tabs = useContext(TabsContext);
  const pane = useRef<HTMLElement>(null);
  const active = tabs?.current === props['data-w-tab'];
  const firstRender = useRef(true);

  useIsomorphicLayoutEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (active && tabs && tabs.durationIn > 0) {
      pane.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: tabs.durationIn, easing: tabs.easing });
    }
  }, [active]);

  return (
    <Tag
      ref={pane}
      {...props}
      role="tabpanel"
      className={cx(withoutClass(className, 'w--tab-active'), active && 'w--tab-active')}
    >
      {children}
    </Tag>
  );
}

export const useTabs = () => useContext(TabsContext);
